import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isFailure, isSuccess } from '@carvana/merch-result';
import { useContext, useEffect } from 'react';
import { useUserLocation } from '~/context/common/UserContext';
import AvailabilityContext, {
  PageAvailabilityData
} from '~/context/home/AvailabilityContext';
import useChunkedBulkAvailability, {
  chunkAvailabilityPayloadsArray
} from '~/hooks/common/services/useChunkedBulkAvailability';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetBulkAvailability() {
  const {
    availabilityPayloads: {
      setData: setPayloads,
      data: payloads,
      isLoaded: isPayloadsLoaded
    },
    carouselVehicleAvailability: {
      data: carouselPayloads,
      isLoaded: isCarouselLoaded
    },
    pageAvailability: { setData, setError, setLoading },
    recentVehicleAvailability: {
      data: recentPayloads,
      isLoaded: isRecentLoaded
    },
    suggestedVehicleAvailability: {
      data: suggestedPayloads,
      isLoaded: isSuggestedLoaded
    }
  } = useContext(AvailabilityContext);
  const { zip } = useUserLocation();

  useEffect(() => {
    if (
      isPayloadsLoaded ||
      !isSuggestedLoaded ||
      !isRecentLoaded ||
      !isCarouselLoaded
    ) {
      return;
    }

    const rawPayloads = [
      ...recentPayloads,
      ...suggestedPayloads,
      ...carouselPayloads
    ];

    const uniquePayloads = rawPayloads.filter((payload, index, self) => {
      // Check if the current index is the first occurrence of the vehicleId
      return self.findIndex(p => p.vehicleId === payload.vehicleId) === index;
    });

    setPayloads(uniquePayloads);
  }, [
    carouselPayloads,
    isCarouselLoaded,
    isPayloadsLoaded,
    isRecentLoaded,
    isSuggestedLoaded,
    payloads,
    recentPayloads,
    setPayloads,
    suggestedPayloads
  ]);

  const chunkFetchBulkAvailability = useChunkedBulkAvailability();

  useEffectWithController(
    controller => {
      if (payloads.length === 0 || !isPayloadsLoaded) return;

      setLoading();

      // Split payloads into chunks of 50(api limit)
      const payloadChunks = chunkAvailabilityPayloadsArray(payloads, 50);

      chunkFetchBulkAvailability(controller, payloadChunks)
        .then(results => {
          // Combine all results from the chunks and convert to a single object so that we only iterate once
          const combinedResults = results.reduce<PageAvailabilityData>(
            (acc, result) => {
              if (isSuccess(result)) {
                result.content.forEach(v => {
                  acc[v.vehicleId] = {
                    soonestPickup: v.soonestPickup,
                    soonestDelivery: v.soonestDelivery
                  };
                });
              } else if (isFailure(result)) {
                setError(new Error(result.errorMessage));
              }

              return acc;
            },
            {}
          );

          setData(combinedResults);
        })
        .catch(
          generateErrorLogger(
            'getBulkAvailability',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      chunkFetchBulkAvailability,
      isPayloadsLoaded,
      payloads,
      setData,
      setError,
      setLoading,
      zip
    ]
  );
}
